import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import SkillsLibrarySection from './skills/SkillsLibrarySection';
import MySkillsSection from './skills/MySkillsSection';
import HowItWorksStrip from './skills/HowItWorksStrip';
import { toast } from 'sonner';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTranslation } from 'react-i18next';
import { Sparkles, Plus, ArrowRight } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Biblioteca de Skills (/prompts):
 *   - EM CIMA: skills do sistema em seções por tipo (admin cadastra;
 *     leitura premium) — SkillsLibrarySection.
 *   - EMBAIXO: Minhas Skills (os prompts do usuário, tabela prompts, com a
 *     mesma experiência visual; aberta a todos) — MySkillsSection.
 * Esta página é só o orquestrador: shell + fetches compartilhados.
 */
const PromptsPage = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle('Biblioteca de Skills');
  const { isPrivate } = usePrivacy();
  const { sidebarWidth, isMobile } = useSidebar();

  const [canais, setCanais] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const skillsRef = useRef(null);

  const checkIfAdmin = useCallback(() => {
    axios.get(`${API}/auth/me`)
      .then((response) => setIsAdmin(response.data.is_super_admin || false))
      .catch(() => setIsAdmin(false));
  }, []);

  const fetchCanais = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/canais`);
      let allCanais = response.data;
      // Cache for sidebar dynamic channel list
      try {
        sessionStorage.setItem('sidebar-channels', JSON.stringify(response.data));
        window.dispatchEvent(new Event('sidebar-channels-updated'));
      } catch {};
      // Include shared channels
      try {
        const sharedResponse = await axios.get(`${API}/canais/compartilhados`);
        const sharedAceitos = (sharedResponse.data || []).filter(c => c.comp_status === 'aceito');
        if (sharedAceitos.length > 0) {
          allCanais = [...allCanais, ...sharedAceitos.map(c => ({ ...c, _is_shared: true }))];
        }
      } catch {}
      setCanais(allCanais);
    } catch (error) {
      toast.error(t('prompts.error_load_channels'));
    }
  }, [t]);

  const fetchPrompts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/prompts/all`);
      setPrompts(response.data);
    } catch (error) {
      toast.error(t('prompts.error_load_prompts'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCanais();
    fetchPrompts();
    checkIfAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
      <Header />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Main Content */}
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Page Header */}
        <PageHeader
          icon={<Sparkles size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title={t('skills.page_title')}
          description={t('skills.page_subtitle')}
        >
          {isAdmin && (
            <button
              type="button"
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={() => skillsRef.current?.openNewSkill()}
            >
              <Plus size={14} />
              {t('skills.admin.new_skill')}
              <ArrowRight size={14} />
            </button>
          )}
        </PageHeader>

        {/* Como funciona: conecta a biblioteca ao fluxo SRT do sistema */}
        <HowItWorksStrip />

        {/* Skills do sistema (painéis por tipo; premium) */}
        <SkillsLibrarySection
          ref={skillsRef}
          isAdmin={isAdmin}
          canais={canais}
          onUserSkillsChanged={fetchPrompts}
        />

        {/* Minhas Skills (prompts do usuário; aberta a todos) */}
        <MySkillsSection
          canais={canais}
          prompts={prompts}
          loading={loading}
          onChanged={fetchPrompts}
          isPrivate={isPrivate}
        />
      </main>
    </div>
  );
};

export default PromptsPage;
